import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { StatusBadge } from '@immo/ui-kit';
import type { LandIqDataRun, LandIqDataRunDetail } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { RunEstimate } from '@/components/landiq/run-estimate';
import { formatDuration } from '@/lib/landiq/format-duration';
import { isSourceOutage, SOURCE_OUTAGE_TONE, STATE_TONES } from '@/lib/landiq/entry-state-tones';

const NOT_AVAILABLE = '—';

type Props = {
  readonly run: LandIqDataRun;
  readonly progress: LandIqDataRunDetail['progress'];
};

export function RunFacts({ run, progress }: Props) {
  const t = useTranslations('pages.admin.landiq-data-run');
  const tList = useTranslations('pages.admin.landiq-data-runs');
  const intl = useIntl();
  const formatTime = (iso: string) =>
    intl.formatDate(iso, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Berlin',
    });
  const finishedAt = run.finishedAt === null ? Date.now() : Date.parse(run.finishedAt);

  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-md border border-border bg-surface px-4 py-3 text-sm">
      <Fact label={t('facts.state')}>
        {isSourceOutage(run.state, run.failureReason) ? (
          <StatusBadge label={tList('failure-reasons.SOURCE_OUTAGE')} tone={SOURCE_OUTAGE_TONE} size="sm" />
        ) : (
          <StatusBadge label={tList(`states.${run.state}`)} tone={STATE_TONES[run.state]} size="sm" />
        )}
      </Fact>
      <Fact label={t('facts.origin')}>{t(`origins.${run.origin}`)}</Fact>
      <Fact label={t('facts.job-status')}>
        <span className="font-mono">{progress?.jobStatus ?? NOT_AVAILABLE}</span>
      </Fact>
      <Fact label={t('facts.duration')}>{formatDuration(finishedAt - Date.parse(run.startedAt))}</Fact>
      {run.estimate !== null && (
        <Fact label={t('metrics.estimate.label')}>
          <RunEstimate estimate={run.estimate} running={run.state === 'RUNNING'} />
        </Fact>
      )}
      <Fact label={t('facts.started')}>{formatTime(run.startedAt)}</Fact>
      <Fact label={t('facts.finished')}>{run.finishedAt === null ? NOT_AVAILABLE : formatTime(run.finishedAt)}</Fact>
      <Fact label={t('facts.render-job')}>
        <span className="font-mono">{run.renderJobId ?? NOT_AVAILABLE}</span>
      </Fact>
      {run.error !== null && (
        <Fact label={t('facts.error')} className="col-span-2">
          <span className="font-mono text-danger-700">{run.error}</span>
        </Fact>
      )}
    </dl>
  );
}

function Fact({
  label,
  children,
  className,
}: {
  readonly label: string;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <div className={`flex items-baseline gap-3 ${className ?? ''}`}>
      <dt className="w-[120px] shrink-0 text-text-tertiary">{label}</dt>
      <dd className="min-w-0 break-words text-text-primary">{children}</dd>
    </div>
  );
}
